import axios from 'axios';
import { onMounted, ref } from 'vue';
import { longDate } from '../helpers/usDate';

/**
 * The policyholder's Library (XN-4560, pulled forward into XN-4552): the staff
 * Files switch, and every file the viewer can see. The Library list and a
 * file's own page (/hub/library/:id) both read it; each loads its own copy.
 */

const TYPE_LABELS = {
  'application/pdf': 'PDF',
  'application/msword': 'Word',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'Word',
  'application/vnd.ms-excel': 'Excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'Excel',
  'application/vnd.ms-powerpoint': 'PowerPoint',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation': 'PowerPoint',
  'text/plain': 'Text',
};

const api = () => window.store.event_backend_api;
// ponytail: one page of 100 per list; page through or search server-side
// when a library outgrows that.
const fetchList = (params = {}) => axios
  .get(`${api()}/resources`, { params: { per_page: 100, ...params } })
  .then(({ data }) => data?.data ?? []);

const typeLabel = (r) => {
  if (TYPE_LABELS[r.type]) return TYPE_LABELS[r.type];
  if (String(r.type || '').startsWith('image/')) return 'Image';
  const ext = /\.([a-z0-9]{1,5})(?:[?#].*)?$/i.exec(r.src || '')?.[1] || /\.([a-z0-9]{1,5})$/i.exec(r.name || '')?.[1];
  return ext ? ext.toUpperCase() : 'File';
};

/** "1.2 MB", "240 KB"; empty without a size. */
export const sizeLabel = (bytes) => {
  const n = Number(bytes);
  if (!Number.isFinite(n) || n <= 0) return '';
  if (n < 1024 * 1024) return `${Math.max(1, Math.round(n / 1024))} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
};

const dayStart = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
export const whenLabel = (iso) => {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  const days = Math.round((dayStart(new Date()) - dayStart(date)) / 86400000);
  if (days <= 0) return 'Added today';
  if (days === 1) return 'Added yesterday';
  return longDate(date);
};

/** "PDF · 2.4 MB": the file page's tag and preview line. */
export const typeSize = (r) => [typeLabel(r), sizeLabel(r.size)].filter(Boolean).join(' · ');
/** "PDF · 2.4 MB · Added today": a row's second line. */
export const metaLine = (r) => [typeSize(r), whenLabel(r.created_at)].filter(Boolean).join(' · ');

export default function usePortalLibrary() {
  // The staff Files switch is a display preference, not an access control: the
  // server enforces access per file. null while loading; a failed read shows files.
  const filesOn = ref(null);
  const state = ref('loading'); // loading | ready | error
  const items = ref([]);

  async function load() {
    state.value = 'loading';
    try {
      // For a policyholder the plain list is hub-wide only and scope=mine is
      // the personal set, so they are disjoint. Owner/staff get every member's
      // personal files in the plain list too: merge by id, `mine` wins.
      const [hub, mine] = await Promise.all([fetchList(), fetchList({ scope: 'mine' })]);
      const byId = new Map(hub.map((r) => [r.id, { ...r, mine: false }]));
      mine.forEach((r) => byId.set(r.id, { ...r, mine: true }));
      items.value = [...byId.values()].sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
      state.value = 'ready';
    } catch (e) {
      state.value = 'error';
    }
  }

  onMounted(async () => {
    try {
      const { data } = await axios.get(`${api()}/event-settings`, { params: { fields: 'event_box_resources' } });
      const flag = data?.settings?.event_box_resources;
      filesOn.value = flag === '1' || flag === 1;
    } catch (e) {
      filesOn.value = true;
    }
    if (filesOn.value) void load();
  });

  return { filesOn, state, items, load };
}
